/**
 * canvasUtils.js
 * Toutes les fonctions de rendu canvas (export PDF, preview, règles).
 * Aucun import React — utilisable partout.
 */
import { fitImage, MM_TO_PX, CM_TO_PX } from '../store/studioStore';
// ── Image cache ──────────────────────────────────────────────────────────────
const imgCache = new Map();
export function loadImage(src) {
  if (imgCache.has(src)) return Promise.resolve(imgCache.get(src));
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // ← Required for canvas drawImage with external URLs
    img.onload = () => {
      imgCache.set(src, img);
      resolve(img);
    };
    img.onerror = (err) => {
      console.error('Failed to load image:', src, err);
      reject(err);
    };
    img.src = src; // ← Must be set AFTER crossOrigin
  });
}

// ── roundRect polyfill ───────────────────────────────────────────────────────
export function roundRect(ctx, x, y, w, h, radii) {
  // Support both single radius and individual corner radii
  let topLeft, topRight, bottomRight, bottomLeft;

  if (typeof radii === 'number') {
    // Single radius for all corners
    topLeft =
      topRight =
      bottomRight =
      bottomLeft =
        Math.max(0, Math.min(radii || 0, w / 2, h / 2));
  } else if (radii && typeof radii === 'object') {
    // Individual corner radii
    topLeft = radii.topLeft || 0;
    topRight = radii.topRight || 0;
    bottomRight = radii.bottomRight || 0;
    bottomLeft = radii.bottomLeft || 0;
  } else {
    topLeft = topRight = bottomRight = bottomLeft = 0;
  }

  if (
    topLeft === 0 &&
    topRight === 0 &&
    bottomRight === 0 &&
    bottomLeft === 0
  ) {
    ctx.rect(x, y, w, h);
    return;
  }

  ctx.moveTo(x + topLeft, y);
  ctx.lineTo(x + w - topRight, y);
  if (topRight > 0) ctx.arcTo(x + w, y, x + w, y + topRight, topRight);
  ctx.lineTo(x + w, y + h - bottomRight);
  if (bottomRight > 0)
    ctx.arcTo(x + w, y + h, x + w - bottomRight, y + h, bottomRight);
  ctx.lineTo(x + bottomLeft, y + h);
  if (bottomLeft > 0) ctx.arcTo(x, y + h, x, y + h - bottomLeft, bottomLeft);
  ctx.lineTo(x, y + topLeft);
  if (topLeft > 0) ctx.arcTo(x, y, x + topLeft, y, topLeft);
  ctx.closePath();
}

// ── Text helpers ─────────────────────────────────────────────────────────────
export function wrapText(ctx, text, maxWidth) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    const test = (line ? line + ' ' : '') + word;
    if (ctx.measureText(test).width > maxWidth - 8 && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  return lines;
}

export function resolveText(item, db) {
  let text = item.text || '';

  // First, handle legacy dbField. It may be stored either as the bare field
  // name ("db_cepage") or wrapped in mustache syntax ("{{db_cepage}}") — the
  // RightPanel field picker saves the wrapped form, so both must be matched
  // against db.fields or the binding silently fails to resolve.
  if (item.dbField) {
    const fieldName =
      item.dbField.startsWith('{{') && item.dbField.endsWith('}}')
        ? item.dbField.slice(2, -2)
        : item.dbField;
    if (db?.fields?.includes(fieldName)) {
      const rec = db.records?.[db.currentRecord];
      if (rec && rec[fieldName] !== undefined) {
        text = String(rec[fieldName]);
      }
    }
  }

  // Then, replace dynamic variables like {{company_name}}, {{type}}, etc.
  if (db?.records && db.currentRecord >= 0) {
    const rec = db.records?.[db.currentRecord];
    if (rec) {
      text = String(text).replace(/\{\{(\w+)\}\}/g, (match, varName) => {
        return rec[varName] !== undefined ? String(rec[varName]) : match;
      });
    }
  }

  return String(text);
}

// ── Border helpers ───────────────────────────────────────────────────────────
function borderDash(style) {
  return { dashed: '10,6', dotted: '2,5' }[style] || '';
}

// ── Draw single item ─────────────────────────────────────────────────────────
export async function drawItemToCanvas(ctx, item, db) {
  if (item.visible === false) return;
  ctx.save();
  ctx.globalAlpha = item.opacity ?? 1;

  // Apply rotation if present
  if (item.rotation && item.rotation !== 0) {
    const centerX = item.x + item.w / 2;
    const centerY = item.y + item.h / 2;
    ctx.translate(centerX, centerY);
    ctx.rotate((item.rotation * Math.PI) / 180);
    ctx.translate(-centerX, -centerY);
  }

  if (item.type === 'text') {
    ctx.fillStyle = item.color || '#111';
    ctx.font = `${item.fontWeight || 400} ${item.fontSize || 24}px ${
      item.fontFamily || 'Marianne'
    }`;
    const lines = wrapText(ctx, resolveText(item, db), item.w);
    const lh = (item.fontSize || 24) * 1.25;
    lines.forEach((line, i) =>
      ctx.fillText(line, item.x + 4, item.y + lh * (i + 0.9))
    );
  } else if (item.type === 'line') {
    // Draw line from (x,y) to (x+w, y+h)
    ctx.beginPath();
    ctx.moveTo(item.x, item.y);
    ctx.lineTo(item.x + item.w, item.y + item.h);
    ctx.strokeStyle = item.stroke || '#000000';
    ctx.lineWidth = item.strokeWidth || 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  } else if (item.type === 'image') {
    const img = await loadImage(item.src);
    ctx.save();
    ctx.beginPath();
    const radii = {
      topLeft: item.radiusTopLeft ?? item.radius ?? 0,
      topRight: item.radiusTopRight ?? item.radius ?? 0,
      bottomRight: item.radiusBottomRight ?? item.radius ?? 0,
      bottomLeft: item.radiusBottomLeft ?? item.radius ?? 0,
    };
    roundRect(ctx, item.x, item.y, item.w, item.h, radii);
    ctx.clip();
    const fit = fitImage(img.width, img.height, item.w, item.h, 'contain');
    ctx.drawImage(img, item.x + fit.x, item.y + fit.y, fit.w, fit.h);
    ctx.restore();
  } else if (item.type === 'rect' || item.type === 'circle') {
    // rect / circle
    ctx.beginPath();
    if (item.type === 'circle') {
      ctx.ellipse(
        item.x + item.w / 2,
        item.y + item.h / 2,
        item.w / 2,
        item.h / 2,
        0,
        0,
        Math.PI * 2
      );
    } else {
      const radii = {
        topLeft: item.radiusTopLeft ?? item.radius ?? 0,
        topRight: item.radiusTopRight ?? item.radius ?? 0,
        bottomRight: item.radiusBottomRight ?? item.radius ?? 0,
        bottomLeft: item.radiusBottomLeft ?? item.radius ?? 0,
      };
      roundRect(ctx, item.x, item.y, item.w, item.h, radii);
    }

    // Fill
    if (item.fill && item.fill !== 'transparent') {
      ctx.fillStyle = item.fill;
      ctx.fill();
    }

    // Stroke
    const hasStroke =
      item.strokeWidth &&
      item.strokeWidth > 0 &&
      item.stroke &&
      item.stroke !== 'transparent';
    if (hasStroke) {
      ctx.strokeStyle = item.stroke;
      const dash = borderDash(item.borderStyle);

      if (item.borderStyle === 'double') {
        ctx.setLineDash([]);
        ctx.lineWidth = item.strokeWidth;
        ctx.stroke();
        ctx.save();
        ctx.beginPath();
        if (item.type === 'circle') {
          ctx.ellipse(
            item.x + item.w / 2,
            item.y + item.h / 2,
            Math.max(0, item.w / 2 - item.strokeWidth * 2.2),
            Math.max(0, item.h / 2 - item.strokeWidth * 2.2),
            0,
            0,
            Math.PI * 2
          );
        } else {
          const innerRadii = {
            topLeft: Math.max(
              0,
              (item.radiusTopLeft ?? item.radius ?? 0) - item.strokeWidth * 1.5
            ),
            topRight: Math.max(
              0,
              (item.radiusTopRight ?? item.radius ?? 0) - item.strokeWidth * 1.5
            ),
            bottomRight: Math.max(
              0,
              (item.radiusBottomRight ?? item.radius ?? 0) -
                item.strokeWidth * 1.5
            ),
            bottomLeft: Math.max(
              0,
              (item.radiusBottomLeft ?? item.radius ?? 0) -
                item.strokeWidth * 1.5
            ),
          };
          roundRect(
            ctx,
            item.x + item.strokeWidth * 1.5,
            item.y + item.strokeWidth * 1.5,
            Math.max(0, item.w - item.strokeWidth * 3),
            Math.max(0, item.h - item.strokeWidth * 3),
            innerRadii
          );
        }
        ctx.lineWidth = Math.max(1, item.strokeWidth * 0.8);
        ctx.stroke();
        ctx.restore();
      } else {
        ctx.lineWidth = item.strokeWidth;
        ctx.setLineDash(dash ? dash.split(',').map(Number) : []);
        ctx.stroke();
      }
    }
  }
  ctx.restore();
}

// ── Page → canvas ────────────────────────────────────────────────────────────
export async function pageToCanvas(
  pageData,
  doc,
  db,
  showCropMarks,
  scale = 2
) {
  const canvas = document.createElement('canvas');
  canvas.width = doc.widthPx * scale;
  canvas.height = doc.heightPx * scale;
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, doc.widthPx, doc.heightPx);

  const bg = pageData.background;
  if (bg && bg.src && bg.visible !== false) {
    try {
      const img = await loadImage(bg.src);
      const W = doc.widthPx;
      const H = doc.heightPx;
      const sc = (bg.scale || 100) / 100;
      const ox = bg.offsetX || 0;
      const oy = bg.offsetY || 0;

      ctx.save();

      // Clip to page bounds so background never bleeds outside
      ctx.beginPath();
      ctx.rect(0, 0, W, H);
      ctx.clip();

      ctx.globalAlpha = bg.opacity ?? 1;

      if (bg.mode === 'stretch') {
        const sw = W * sc;
        const sh = H * sc;
        ctx.drawImage(img, ox + (W - sw) / 2, oy + (H - sh) / 2, sw, sh);
      } else {
        const fit = fitImage(img.width, img.height, W, H, bg.mode || 'contain');
        const sw = fit.w * sc;
        const sh = fit.h * sc;
        const cx = fit.x + fit.w / 2;
        const cy = fit.y + fit.h / 2;
        ctx.drawImage(img, ox + cx - sw / 2, oy + cy - sh / 2, sw, sh);
      }

      ctx.restore();
    } catch (err) {
      console.warn('Background image failed to load:', bg.src, err);
      // Gracefully skip background if image fails
    }
  }

  for (const item of pageData.elements) {
    await drawItemToCanvas(ctx, item, db);
  }

  if (showCropMarks) drawCropMarksOnCanvas(ctx, doc);
  return canvas;
}

function drawCropMarksOnCanvas(ctx, doc) {
  const W = doc.widthPx;
  const H = doc.heightPx;
  ctx.save();
  ctx.fillStyle = '#333';
  [
    [0, 0],
    [W - 25, 0],
    [0, H - 2],
    [W - 25, H - 2],
  ].forEach(([x, y]) => ctx.fillRect(x, y, 25, 2));
  [
    [0, 0],
    [W - 2, 0],
    [0, H - 25],
    [W - 2, H - 25],
  ].forEach(([x, y]) => ctx.fillRect(x, y, 2, 25));
  ctx.restore();
}

// ── Ruler ticks ──────────────────────────────────────────────────────────────
export function drawRulerTicks(ctx, dir, totalPx, docPx, zoom, offset) {
  const docCm = docPx / CM_TO_PX;
  const intervals = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20];
  const minSpacing = 40;
  const step = intervals.find((s) => s * CM_TO_PX * zoom >= minSpacing) || 20;
  const majorEvery = step < 1 ? 1 : 5;

  ctx.fillStyle = '#6b7280';
  ctx.font = '8px sans-serif';
  ctx.textAlign = dir === 'h' ? 'center' : 'right';
  ctx.textBaseline = dir === 'h' ? 'bottom' : 'middle';

  for (let i = 0; i * step <= docCm + step * 0.01; i++) {
    const cm = Math.round(i * step * 1000) / 1000;
    const pos = Math.round(offset + cm * CM_TO_PX * zoom);
    if (pos < 0 || pos > totalPx) continue;
    const isMajor = i % majorEvery === 0;
    const tickLen = isMajor ? 12 : 5;
    const label = Number.isInteger(cm) ? String(cm) : cm.toFixed(1);

    if (dir === 'h') {
      ctx.fillRect(pos, 20 - tickLen, 1, tickLen);
      if (isMajor) ctx.fillText(label, pos, 20 - tickLen - 1);
    } else {
      ctx.fillRect(20 - tickLen, pos, tickLen, 1);
      if (isMajor) {
        ctx.save();
        ctx.translate(20 - tickLen - 2, pos);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(label, 0, 0);
        ctx.restore();
      }
    }
  }
}

// ── File helpers ─────────────────────────────────────────────────────────────
export function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Export PDF ───────────────────────────────────────────────────────────────
export async function exportPdf(
  pages,
  doc,
  db,
  showCropMarks,
  fileName = 'studio-pro-export',
  returnBlob = false,
  whiteSpace = true
) {
  const { jsPDF } = await import('jspdf');
  const wMm = doc.widthMm || doc.widthPx / MM_TO_PX;
  const hMm = doc.heightMm || doc.heightPx / MM_TO_PX;
  // eslint-disable-next-line new-cap
  const PDF = new jsPDF({
    orientation: wMm >= hMm ? 'landscape' : 'portrait',
    unit: 'mm',
    format: [wMm, hMm],
  });

  const margin = 10;

  for (let i = 0; i < pages.length; i++) {
    if (i > 0) PDF.addPage([wMm, hMm], wMm >= hMm ? 'landscape' : 'portrait');
    const canvas = await pageToCanvas(pages[i], doc, db, showCropMarks, 2);
    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    if (whiteSpace) {
      const imgWidth = wMm - margin * 2;
      const imgHeight = hMm - margin * 2;

      PDF.addImage(imgData, 'JPEG', margin, margin, imgWidth, imgHeight);
    } else {
      PDF.addImage(imgData, 'JPEG', 0, 0, wMm, hMm);
    }
  }

  if (returnBlob) {
    return PDF.output('blob');
  }

  PDF.save(`${fileName}.pdf`);
}

export async function exportPdfsZip(
  pages,
  doc,
  db,
  showCropMarks,
  fileName = 'studio-pro-export',
  eventId = null,
  exhibitorRepository,
  whiteSpace = true // ✅ Added to match exportPdf behaviour
) {
  if (eventId) {
    const response = await exhibitorRepository.eventShowroomProducts(eventId);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');

    const contentType = response.headers['content-type'];

    let fileName = `product_${new Date().getTime()}`;

    if (contentType.includes('pdf')) {
      fileName += '.pdf';
    } else {
      fileName += '.zip';
    }

    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}
